import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, PartyPopper } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneVerifyDialog } from "@/components/dialogs";
import { CitySelect } from "@/components/community";
import { cn, Spinner } from "@/components/common";

export default function Onboarding() {
  const nav = useNavigate();
  const { config, refreshUser, user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ first_name: "", last_name: "", state: "", city: "", postcode: "", country: "Australia", interests: [] });
  const [loading, setLoading] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);

  const intents = config?.onboarding_intents || [];
  const toggle = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));
  const setLocation = (o) => setForm((f) => ({ ...f, city: o.locality, state: o.state, postcode: o.postcode, country: "Australia" }));

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/auth/onboarding", form);
      await refreshUser();
      setStep(4);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={cn("h-1.5 flex-1 rounded-full transition-colors", s <= step ? "bg-primary" : "bg-border")} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-5">
            <div><h1 className="font-display text-3xl font-extrabold">Welcome to Mtaa 🇰🇪🇦🇺</h1><p className="mt-1 text-muted-foreground">Let's set up your profile. First, your name.</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="ob-firstname" /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="ob-lastname" /></div>
            </div>
            <Button className="w-full" onClick={() => setStep(2)} data-testid="ob-next-1">Continue <ArrowRight className="h-4 w-4" /></Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div><h1 className="font-display text-3xl font-extrabold">Where's your Mtaa?</h1><p className="mt-1 text-muted-foreground">Search any Australian suburb, town or city. Your exact street address is never stored or shown.</p></div>
            <div>
              <Label>Community</Label>
              <CitySelect value={form.city ? `${form.city}, ${form.state} ${form.postcode}` : ""} onChange={setLocation} testid="ob-city-select" />
              {form.city && <p className="mt-2 text-sm text-brand-green" data-testid="ob-city-selected">Selected: {form.city}, {form.state} {form.postcode}</p>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button className="flex-1" onClick={() => setStep(3)} disabled={!form.city} data-testid="ob-next-2">Continue <ArrowRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div><h1 className="font-display text-3xl font-extrabold">What brings you to Mtaa?</h1><p className="mt-1 text-muted-foreground">Choose all that apply.</p></div>
            <div className="grid grid-cols-2 gap-2">
              {intents.map((i) => (
                <button key={i} onClick={() => toggle(i)} data-testid={`ob-intent-${i}`}
                  className={cn("flex items-center justify-between rounded-xl border p-3 text-left text-sm font-medium transition-all", form.interests.includes(i) ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40")}>
                  {i}{form.interests.includes(i) && <Check className="h-4 w-4" />}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button className="flex-1" onClick={finish} disabled={loading} data-testid="ob-finish">{loading ? <Spinner className="text-white" /> : "Finish"}</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10"><PartyPopper className="h-10 w-10 text-primary" /></div>
            <div><h1 className="font-display text-3xl font-extrabold">You're in 🎉</h1><p className="mt-1 text-muted-foreground">Verify your phone now to unlock selling, business & service posting — or do it later.</p></div>
            <Button className="w-full" onClick={() => setPhoneOpen(true)} data-testid="ob-verify-phone">Verify phone number</Button>
            <Button variant="ghost" className="w-full" onClick={() => nav("/app", { replace: true })} data-testid="ob-skip">Take me to Mtaa</Button>
          </div>
        )}
      </div>
      <PhoneVerifyDialog open={phoneOpen} onOpenChange={setPhoneOpen} onVerified={() => nav("/app", { replace: true })} />
    </div>
  );
}
